import {
  Box,
  Button,
  Center,
  Flex,
  FormLabel,
  Input,
  Select,
  Text,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { findKey, isEmpty, toNumber, toString } from "lodash";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import {
  getBookingEngineHotelConfig,
  getBookingEngineLocale,
  getBookingEngineNights,
  getLocalStorage,
  increaseDate,
  setLocalStorage,
  submitBookingEngineForm,
} from "../utils/functions";
import { useGetListHotel, useLocale } from "../utils/hooks";
import LoadingSpinner from "./LoadingSpinner";

const UnsafeFlex = Flex as any;
const UnsafeCenter = Center as any;
const UnsafeFormLabel = FormLabel as any;
const UnsafeButton = Button as any;
const UnsafeSelect = Select as any;
const UnsafeInput = Input as any;
const UnsafeText = Text as any;

export default function FormBookNowTop({
  hotelCode,
  colorPrimary,
}: {
  hotelCode: any;
  colorPrimary: any;
}) {
  const router = useRouter();
  const locale = useLocale();

  const [minDate, setMinDate] = useState("");

  useEffect(() => {
    setMinDate(new Date().toISOString().split("T")[0]);
  }, []);

  const { hotels, isLoading } = useGetListHotel();

  const [selectedHotel, setSelectedHotel] = useState<any>("-1");

  useEffect(() => {
    if (hotels && hotelCode) {
      const temp = findKey(hotels, function (o: any) {
        return o.hotel_code == hotelCode;
      });
      if (temp !== undefined) {
        setSelectedHotel(toString(temp));
      }
    }
  }, [hotels, hotelCode]);

  useEffect(() => {
    if (hotels && selectedHotel !== -1 && selectedHotel !== "-1" && hotels[selectedHotel]) {
      formik.setValues({
        ...formik.values,
        hotel: hotels[selectedHotel],
      });
    }
  }, [selectedHotel, hotels]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      arrival: "",
      departure: "",
      hotel: hotels ? hotels[0] : {},
      rooms: 1,
      adults: 2,
      child: 0,
      voucher: "",
    },
    validationSchema: Yup.object({
      arrival: Yup.string().required(
        locale == "id" ? "Harus diisi" : "Required"
      ),
      departure: Yup.string().required(
        locale == "id" ? "Harus diisi" : "Required"
      ),
      hotel: Yup.object(),
      rooms: Yup.number(),
      adults: Yup.number(),
      child: Yup.number(),
      voucher: Yup.string(),
    }),
    onSubmit: () => {},
  });

  // Ambil nilai persistensi dari localStorage saat client-side mount
  useEffect(() => {
    const savedArrival = getLocalStorage("booking_arrival", "");
    const savedDeparture = getLocalStorage("booking_departure", "");
    if (savedArrival || savedDeparture) {
      formik.setValues({
        ...formik.values,
        arrival: savedArrival,
        departure: savedDeparture,
      });
    }
  }, []);

  function handleClick() {
    const { adults, arrival, child, departure, hotel, voucher } = formik.values;

    const bookingHotelCode = hotel?.hotel_code || hotels?.[0]?.hotel_code;
    const bookingConfig = getBookingEngineHotelConfig(bookingHotelCode);
    if (!bookingConfig) return;

    submitBookingEngineForm({
      action: bookingConfig.action,
      fields: {
        language: getBookingEngineLocale(router.locale),
        arrivalDate: isEmpty(arrival) ? "" : arrival,
        nights: getBookingEngineNights(arrival, departure),
        guestCountSelector: "ReadOnly",
        crossSell: "false",
        selectedAdultCount: adults,
        selectedChildCount: child,
        rate: voucher,
      },
    });
  }

  // Efek untuk menyinkronkan data tanggal ke localStorage saat ada perubahan
  useEffect(() => {
    if (formik.values.arrival) {
      setLocalStorage("booking_arrival", formik.values.arrival);
    }
  }, [formik.values.arrival]);

  useEffect(() => {
    if (formik.values.departure) {
      setLocalStorage("booking_departure", formik.values.departure);
    }
  }, [formik.values.departure]);

  if (isLoading || !hotels || !selectedHotel)
    return (
      <UnsafeFlex
        bg={colorPrimary}
        color="white"
        py={6}
        px={5}
        borderRadius={10}
        flexDir="column"
        gap={4}
        w="100%"
        fontSize="sm"
        // opacity={0.9}
      >
        <UnsafeCenter h={10}>
          <LoadingSpinner />
        </UnsafeCenter>
      </UnsafeFlex>
    );

  return (
    <UnsafeFlex
      bg={colorPrimary}
      color="white"
      py={6}
      px={5}
      borderRadius={10}
      flexDir="column"
      gap={4}
      w="100%"
      maxW={600}
      fontSize="sm"
      opacity={0.9}
      border="1px solid var(--chakra-colors-whiteAlpha-600)"
    >
      <UnsafeFlex
        gap={[0, null, null, 8]}
        w="full"
        flexWrap={["wrap", null, null, "nowrap"]}
      >
        <UnsafeFlex
          flexDir="column"
          w={["full", null, null, "50%"]}
          mb={[3, null, null, 0]}
        >
          <UnsafeFormLabel fontSize="xs" textTransform="uppercase" fontWeight={500}>
            {locale == "id" ? "Kedatangan" : "Arrival"}
          </UnsafeFormLabel>
          <Box>
            <UnsafeInput
              textTransform="uppercase"
              fontSize="sm"
              color="white"
              colorScheme="white"
              type="date"
              lang="fr-CA"
              borderRadius={0}
              borderColor="whiteAlpha.600"
              _placeholder={{ color: "white", opacity: 1 }}
              style={{ colorScheme: "dark" }}
              min={minDate}
              onChange={(e: any) => {
                const nextDate = increaseDate(e.target.value);
                formik.setValues({
                  ...formik.values,
                  arrival: e.target.value,
                  departure: nextDate,
                });
                setLocalStorage("booking_arrival", e.target.value);
                setLocalStorage("booking_departure", nextDate);
              }}
              id="arrival"
              name="arrival"
              value={formik.values.arrival}
            />
            <UnsafeText fontSize="xs" color="red.500" mt={1}>
              {formik.errors.arrival}
            </UnsafeText>
          </Box>
        </UnsafeFlex>

        <UnsafeFlex
          flexDir="column"
          w={["full", null, null, "50%"]}
          mb={[3, null, null, 0]}
        >
          <UnsafeFormLabel fontSize="xs" textTransform="uppercase" fontWeight={500}>
            {locale == "id" ? "Kepergian" : "Departure"}
          </UnsafeFormLabel>
          <Box>
            <UnsafeInput
              textTransform="uppercase"
              fontSize="sm"
              disabled={!formik.values.arrival}
              min={
                new Date(
                  new Date().setDate(
                    new Date(
                      formik.values.arrival == ""
                        ? new Date()
                        : formik.values.arrival
                    ).getDate() + 1
                  )
                )
                  .toISOString()
                  .split("T")[0]
              }
              color="white"
              colorScheme="white"
              type="date"
              borderRadius={0}
              borderColor="whiteAlpha.600"
              _placeholder={{ color: "white", opacity: 1 }}
              style={{ colorScheme: "dark" }}
              onChange={(e: any) => {
                formik.setValues({
                  ...formik.values,
                  departure: e.target.value,
                });
              }}
              id="departure"
              name="departure"
              value={formik.values.departure}
            />
            <UnsafeText fontSize="xs" color="red.500" mt={1}>
              {formik.errors.departure}
            </UnsafeText>
          </Box>
        </UnsafeFlex>
      </UnsafeFlex>

      <UnsafeFlex flexDir="column" w="100%" mb={[3, null, null, 0]}>
        <UnsafeFormLabel fontSize="xs" textTransform="uppercase" fontWeight={500}>
          {locale == "id" ? "Pilih Hotel" : "Choose Hotel"}
        </UnsafeFormLabel>
        <Box>
          <UnsafeSelect
            fontSize="sm"
            color="white"
            colorScheme="white"
            borderRadius={0}
            borderColor="whiteAlpha.600"
            _focus={{}}
            // defaultValue={selectedHotel}
            value={selectedHotel}
            onChange={(e: any) => {
              const val = e.target.value;
              setSelectedHotel(val);
              if (hotels && hotels[val]) {
                formik.setValues({
                  ...formik.values,
                  hotel: hotels[val],
                });
              }
            }}
          >
            {hotels?.map((hotel: any, i: any) => {
              return (
                <option key={hotel.name} value={i} style={{ color: "black" }}>
                  {hotel.name}
                </option>
              );
            })}
          </UnsafeSelect>
        </Box>
      </UnsafeFlex>

      <UnsafeFlex gap={8} w="full">
        <UnsafeFlex flexDir="column" w="33%" mb={[3, null, null, 0]}>
          <UnsafeFormLabel fontSize="xs" textTransform="uppercase" fontWeight={500}>
            {locale == "id" ? "Kamar" : "Rooms"}
          </UnsafeFormLabel>
          <Box>
            <UnsafeInput
              fontSize="sm"
              color="white"
              colorScheme="white"
              type="number"
              borderRadius={0}
              borderColor="whiteAlpha.600"
              _placeholder={{ color: "white", opacity: 1 }}
              placeholder="1"
              onChange={(e: any) => {
                formik.setValues({
                  ...formik.values,
                  rooms: toNumber(e.target.value),
                });
              }}
            />
            <UnsafeText fontSize="xs" color="red.500" mt={1}>
              {formik.errors.rooms}
            </UnsafeText>
          </Box>
        </UnsafeFlex>{" "}
        <UnsafeFlex flexDir="column" w="33%" mb={[3, null, null, 0]}>
          <UnsafeFormLabel fontSize="xs" textTransform="uppercase" fontWeight={500}>
            {locale == "id" ? "Dewasa" : "Adults"}
          </UnsafeFormLabel>
          <Box>
            <UnsafeInput
              fontSize="sm"
              color="white"
              colorScheme="white"
              type="number"
              borderRadius={0}
              borderColor="whiteAlpha.600"
              _placeholder={{ color: "white", opacity: 1 }}
              placeholder="2"
              onChange={(e: any) => {
                formik.setValues({
                  ...formik.values,
                  adults: toNumber(e.target.value),
                });
              }}
            />
            <UnsafeText fontSize="xs" color="red.500" mt={1}>
              {formik.errors.adults}
            </UnsafeText>
          </Box>
        </UnsafeFlex>{" "}
        <UnsafeFlex flexDir="column" w="33%" mb={[3, null, null, 0]}>
          <UnsafeFormLabel fontSize="xs" textTransform="uppercase" fontWeight={500}>
            {locale == "id" ? "Anak" : "Child"}
          </UnsafeFormLabel>
          <Box>
            <UnsafeInput
              fontSize="sm"
              color="white"
              colorScheme="white"
              type="number"
              borderRadius={0}
              borderColor="whiteAlpha.600"
              _placeholder={{ color: "white", opacity: 1 }}
              placeholder="0"
              onChange={(e: any) => {
                formik.setValues({
                  ...formik.values,
                  child: toNumber(e.target.value),
                });
              }}
            />
            <UnsafeText fontSize="xs" color="red.500" mt={1}>
              {formik.errors.child}
            </UnsafeText>
          </Box>
        </UnsafeFlex>
      </UnsafeFlex>

      <UnsafeFlex flexDir="column" w="100%" mb={[3, null, null, 0]}>
        <UnsafeFormLabel fontSize="xs" textTransform="uppercase" fontWeight={500}>
          {locale == "id" ? "Kode Promo" : "Promo Code"}
        </UnsafeFormLabel>
        <Box>
          <UnsafeInput
            fontSize="sm"
            color="white"
            colorScheme="white"
            type="text"
            borderRadius={0}
            borderColor="whiteAlpha.600"
            placeholder={
              locale == "id" ? "Masukkan Kode" : "Insert Code"
            }
            _placeholder={{ color: "white", opacity: 1 }}
            onChange={(e: any) => {
              formik.setValues({
                ...formik.values,
                voucher: e.target.value,
              });
            }}
          />
          <UnsafeText fontSize="xs" color="red.500" mt={1}>
            {formik.errors.voucher}
          </UnsafeText>
        </Box>
      </UnsafeFlex>

      <UnsafeCenter>
        <UnsafeButton
          variant="solid"
          bg="white"
          color="black"
          onClick={handleClick}
          disabled={!formik.isValid || isEmpty(formik.values.arrival)}
        >
          {locale == "id" ? "Pesan" : "Book Now"}
        </UnsafeButton>
      </UnsafeCenter>
    </UnsafeFlex>
  );
}
